import { api, clearRefreshBroadcast } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import { env } from '@/lib/env';
import { clearLogoutPending, isLogoutPending, markLogoutPending } from './logout-pending';
import { setAccessToken } from './token-store';
import type { AuthAudience } from './types';

/**
 * Pluggable authentication strategy. LOCAL (password + 2FA in the SPA) and OIDC
 * (backend-mediated redirect) share this seam; the active one is chosen from
 * the public `GET /auth/mode`.
 */
export interface AuthStrategy {
  readonly kind: 'oidc' | 'local';
  /** Begin login. OIDC redirects the whole browser; LOCAL is a no-op — the SPA owns the form. */
  login(audience?: AuthAudience): void;
  /** End the session server-side, then clear the in-memory token. Resolves whether the server confirmed (D1). */
  logout(): Promise<boolean>;
}

/**
 * Post `/auth/logout`. True when the server ended the session, or holds none
 * for this browser (401). A network failure or a server error is false: the
 * refresh cookie may still be live. No refresh-and-retry: refreshing here
 * would revive the very session being ended.
 */
async function postLogout(): Promise<boolean> {
  try {
    await api.post('/auth/logout', undefined, { noRetry: true });
    return true;
  } catch (error) {
    return error instanceof ApiError && error.status === 401;
  }
}

/**
 * End the session on the server and in this page (#119 E2E D1). The pending
 * mark goes down before the request and is lifted only on the server's answer,
 * so a sign-out that cannot reach the server (offline "Sign out anyway", or a
 * page closed mid-request) is finished by `settlePendingLogout` before anything
 * uses the refresh cookie again. Resolves whether the server confirmed.
 */
export async function endServerSession(): Promise<boolean> {
  markLogoutPending();
  const ended = await postLogout();
  if (ended) clearLogoutPending();
  setAccessToken(null);
  clearRefreshBroadcast();
  return ended;
}

/**
 * Finish a sign-out the server never confirmed (D1). Runs before the boot's
 * session restore and before every sign-in. True when no sign-out is pending
 * any more; false (still offline, server error) means the old session may be
 * alive: do not restore it, and try again on the next attempt.
 */
export async function settlePendingLogout(): Promise<boolean> {
  if (!isLogoutPending()) return true;
  const ended = await postLogout();
  if (ended) clearLogoutPending();
  return ended;
}

/**
 * Always starts the backend-mediated OIDC flow (used by the SSO button).
 * `clientNonce` comes back in the callback's fragment only when the login
 * completes (#119 E2E D1).
 */
export function startOidcLogin(audience: AuthAudience = 'APPLICANT', clientNonce?: string): void {
  const nonce = clientNonce ? `&client_nonce=${encodeURIComponent(clientNonce)}` : '';
  window.location.assign(`${env.VITE_API_URL}/auth/oidc/start?audience=${audience}${nonce}`);
}

/**
 * Backend-mediated OIDC. We hand the browser to `/auth/oidc/start`; the backend
 * runs the code+PKCE flow, sets the httpOnly refresh cookie, and redirects back to
 * the SPA. IdP tokens never touch the browser.
 */
export class OidcRedirectStrategy implements AuthStrategy {
  readonly kind = 'oidc' as const;

  login(audience: AuthAudience = 'APPLICANT'): void {
    startOidcLogin(audience);
  }

  async logout(): Promise<boolean> {
    return endServerSession();
  }
}

/** Password login lives on the sign-in page; this strategy only owns logout. */
export class LocalPasswordStrategy implements AuthStrategy {
  readonly kind = 'local' as const;

  login(_audience?: AuthAudience): void {
    // Intentionally empty — the SPA owns the password form.
  }

  async logout(): Promise<boolean> {
    return endServerSession();
  }
}

/** Resolve the strategy for the platform's current auth mode. */
export function strategyForMode(mode: string): AuthStrategy {
  if (mode === 'LOCAL') return new LocalPasswordStrategy();
  return new OidcRedirectStrategy();
}
